import { PEWTER_GAP, PEWTER_OUTLINE, pewterEdge, pewterFrame, pewterInner, solid, type EdgeStyleName } from "./pewter-edge";

/**
 * The frame every activity card wears: a white band with a pewter hairline
 * running across it, then the card inside at the frame's radius less the band.
 *
 * Shared because there are two activity cards — `ActivityCardDiscover` on the
 * grids and `ActivityCard2` on the home page, account and blog — and a frame
 * defined twice is a frame that drifts.
 */
export const CARD_RADIUS = 22;

/**
 * The sign-in sheet's own tone rather than flat white: cool, faintly lavender,
 * deepening slightly toward the bottom, so a card and a sheet read as the same
 * material.
 */
export const CARD_FILL =
  "linear-gradient(180deg, rgba(252,252,254,1) 0%, rgba(246,246,251,1) 62%, rgba(242,242,248,1) 100%)";

export const CARD_FRAME = pewterFrame(CARD_RADIUS);

/**
 * Shadow A from /card-shadow-lab, picked by the owner on 2026-10-25: the
 * "Monthly $286.60" widget's lift. A wide soft drop pulled in with a negative
 * spread so it pools under the card, plus a short one where it meets the page.
 *
 * Sized to fit the rails. The card rows scroll sideways, and a scroller clips
 * both ways, leaving 32px under a card (pb-8) and 16px at its sides (px-4).
 * The lab's version reached 42px down and was cut into a hard edge, so the
 * drop is 14 + 28 - 10 = 32px: the same look, ending where the rail does.
 * Hover is the same shape, a little deeper, and still inside the rail.
 */
export const CARD_SHADOW = "0 14px 28px -10px rgba(17,14,57,0.34), 0 4px 10px -4px rgba(17,14,57,0.12)";
export const CARD_SHADOW_HOVER = "0 16px 30px -12px rgba(17,14,57,0.40), 0 6px 12px -6px rgba(17,14,57,0.14)";

/**
 * The frame's outer hairline and the card's drop shadow are both box-shadows,
 * so they have to be composed rather than overwrite one another. That leaves
 * the hover state with nothing to write as a Tailwind arbitrary class, hence
 * the custom property `.mj-activity-card:hover` reads (tailwind.css).
 */
/**
 * Both shadows are handed over as custom properties and applied by
 * `.mj-activity-card` in tailwind.css — NOT as an inline `boxShadow`.
 *
 * An inline style wins over any stylesheet rule, so setting the resting shadow
 * inline silently killed `:hover`: the card kept its lift (a class) but never
 * grew its shadow. Custom properties inline are fine; the rule reads them.
 */
export const cardFrameStyle = {
  ...CARD_FRAME,
  boxShadow: undefined,
  ["--mj-card-shadow" as string]: `${CARD_FRAME.boxShadow}, ${CARD_SHADOW}`,
  ["--mj-card-shadow-hover" as string]: `${CARD_FRAME.boxShadow}, ${CARD_SHADOW_HOVER}`,
};

/**
 * The same frame at any radius, for the panels that are not cards — the blog's
 * contents rail, author block and inline booking strip. Quieter shadow: these
 * sit in a column beside prose and do not lift on hover.
 */
const PANEL_SHADOW = "0 8px 30px rgba(17,14,57,0.06)";

/**
 * How the band is lit. `pewter` is the house frame — lit on two opposite
 * corners, so it turns as your eye travels round it. `topLit` is one light
 * from above, brightest along the top edge and softening down.
 *
 * The two are meant to be mixed: `pewter` on the panels you stop and read,
 * `topLit` on a repeating grid of small cards, where four turning bands next
 * to each other read as glitter rather than as material. Only these two and
 * `sheen` ship — gold and iridescent are lab-only (owner, 2026-09-07).
 */
export function panelFrameStyle(radius: number, edge: EdgeStyleName = "pewter") {
  const frame = pewterFrame(radius, { style: edge });
  return { ...frame, boxShadow: `${frame.boxShadow}, ${PANEL_SHADOW}` };
}

export function panelInnerStyle(radius: number, fill: string = CARD_FILL) {
  return pewterInner(fill, radius);
}

/**
 * The frame on a single element, for panels that do not need to clip anything.
 *
 * The band is the element's own transparent border, the fill paints inside it,
 * and the panel's hairline is an *inset* shadow — inset shadows are clipped to
 * the padding box, which is exactly where the panel's edge is. That saves the
 * wrapper div the two-element version needs, so an existing panel becomes
 * framed by swapping its classes for one style.
 */
export function panelStyle(radius: number, fill: string = CARD_FILL, edge: EdgeStyleName = "pewter") {
  return {
    ...pewterEdge(fill, radius, {
      tone: "light",
      style: edge,
      width: PEWTER_GAP,
      base: solid("rgba(255,255,255,1)"),
    }),
    boxShadow: [
      `0 0 0 1px ${PEWTER_OUTLINE}`,
      PANEL_SHADOW,
      "inset 0 0 0 1px rgba(17,14,57,0.13)",
    ].join(", "),
  };
}

/**
 * The photo frame on the grid cards (ActivityCardDiscover, StayCard): the
 * Discover card's shape, where the photo wears the frame on its own and the
 * text sits on the page beneath it.
 *
 * `topLit` and a 6px band, the mobile app's quick-action tiles (owner,
 * 2026-10-06): bright along the top edge, greying toward the bottom, which on
 * a repeating grid reads as one light over the page rather than as glitter.
 *
 * Below 768px the `.mj-photo-frame` / `.mj-photo-frame-inner` classes in
 * tailwind.css narrow it to a 3px band and 20px corners, the app's grid frame:
 * at two cards to a phone row the 6px band read as a thick border.
 */
export const PHOTO_FRAME_RADIUS = 24;
export const PHOTO_FRAME_GAP = 6;

const PHOTO_FRAME_BASE = pewterFrame(PHOTO_FRAME_RADIUS, { gap: PHOTO_FRAME_GAP, style: "topLit" });

export const photoFrameStyle = {
  ...PHOTO_FRAME_BASE,
  // Shadow A, the same lift as CARD_SHADOW (owner, 2026-10-25).
  boxShadow: `0 0 0 1px rgba(17,14,57,0.06), ${CARD_SHADOW}`,
};

/** The photo inside `photoFrameStyle`: radius less the band, one hairline. */
export const photoInnerStyle = pewterInner("rgba(17,14,57,0.055)", PHOTO_FRAME_RADIUS, {
  gap: PHOTO_FRAME_GAP,
  hairline: "rgba(17,14,57,0.10)",
});
